/**
 * One `<title>` per route — TRBUD-242.
 *
 * `index.html` sets `TruckBuddy` and nothing ever changed it, so every screen
 * in the app shared one tab label. That is not merely untidy:
 *
 * - A screen reader announces the document title on navigation. With one title
 *   for twenty-three routes, a client-side navigation is silent about where it
 *   went, which is the accessibility half of the problem and the reason this
 *   is not deferred to the joint UI pass DEC-07 covers. It changes no pixel.
 * - Two tabs open on a queue and a work order are indistinguishable, and so is
 *   every entry in browser history. Brett and Matt work with several open.
 *
 * ── Why a pure function plus a thin hook ───────────────────────────
 * `titleForPath` takes a pathname and returns a string, with no React and no
 * DOM, so the mapping can be asserted in a plain unit test — the harness under
 * `apps/web/src/test/` installs a DOM per file, and a test that had to mount a
 * router to check a string would be testing the router. The hook is three
 * lines on top of it.
 *
 * The labels come from `NAVIGATION_ITEMS` in `@truckbuddy/contract` wherever a
 * route has a nav entry, deliberately: the tab strip and the tab title are the
 * same name for the same place, and deriving one from the other is what stops
 * them drifting apart. Only the routes with NO nav entry — the detail screens
 * and the Settings sub-pages — are named here.
 */
import { useEffect } from "react";
import { NAVIGATION_ITEMS } from "@truckbuddy/contract";

/** Appended to every title. Kept short: a tab shows very few characters. */
export const TITLE_SUFFIX = "TruckBuddy";

/**
 * Routes with no entry in `NAVIGATION_ITEMS`, longest path first.
 *
 * Ordered longest-first so `/settings/labour-rates` is matched before
 * `/settings`. A prefix table matched in declaration order is the whole reason
 * this is an array rather than a record — a record's key order is not a
 * contract and the shorter prefix would win at random.
 */
const EXTRA_ROUTES: ReadonlyArray<readonly [string, string]> = [
  ["/settings/connections/manage", "Manage API connections"],
  ["/settings/connections", "Connections & bots"],
  ["/reports/mainx-review", "MaintainX expense review"],
  // TRBUD-467. Before the "Saved report" prefix, which would otherwise name it.
  ["/reports/receipt-accuracy", "AI receipt accuracy"],
  ["/reports/new", "New report"],
  ["/reports/", "Saved report"],
  ["/settings/labour-rates", "Labour rates"],
  ["/settings/pm-bonus", "PM bonus rate"],
  ["/settings/collectors", "Collectors"],
  ["/settings/document-scans", "Document scans"],
  ["/settings/permissions", "Permissions"],
  ["/settings/vendor-matches", "Vendor matches"],
  ["/settings/users", "Users"],
  ["/settings/maintenance", "Maintenance mode"],
  // TRBUD-466. Under `/vendors/`, so without this it would read "Vendors".
  ["/vendors/completeness", "Vendor data completeness"],
  ["/work-orders/", "Work order"],
  ["/assets/", "Asset record"],
  ["/review/new", "Log an invoice"],
  // The pop-out viewer; it sets a fuller title once it knows the document.
  ["/review/viewer", "Receipt viewer"],
];

/**
 * The tab title for a pathname.
 *
 * `/` returns the bare product name: it is a redirect, and by the time a
 * person could read a title the router has already moved them on, so naming it
 * "Home" would flash a screen that does not exist.
 */
export function titleForPath(pathname: string): string {
  if (pathname === "/" || pathname === "") return TITLE_SUFFIX;

  // A menu entry's own path first (TRBUD-439). The Reports pages sit UNDER
  // `/reports/`, so the "Saved report" prefix below would otherwise name
  // `/reports/spend` after a saved report.
  const exact = NAVIGATION_ITEMS.find((item) => item.path === pathname);
  if (exact) return `${exact.label} · ${TITLE_SUFFIX}`;

  // `/reports` itself has no menu entry since the menu names its three pages;
  // it is the Reports landing, so it says so. Exact, so `/reportsx` is not it.
  if (pathname === "/reports") return `Reports · ${TITLE_SUFFIX}`;

  for (const [prefix, label] of EXTRA_ROUTES) {
    if (pathname === prefix || pathname.startsWith(prefix)) return `${label} · ${TITLE_SUFFIX}`;
  }

  // Then the longest matching prefix, so a future nested route inherits its
  // parent's name rather than falling through to the generic case.
  const nested = [...NAVIGATION_ITEMS]
    .filter((item) => pathname.startsWith(`${item.path}/`))
    .sort((a, b) => b.path.length - a.path.length)[0];
  if (nested) return `${nested.label} · ${TITLE_SUFFIX}`;

  // Anything left is a URL with no screen behind it. Saying so is more useful
  // than the product name, because the tab is often the only place a mistyped
  // link is still visible.
  return `Not found · ${TITLE_SUFFIX}`;
}

/** Set `document.title` from the current pathname. */
export function useDocumentTitle(pathname: string): void {
  useEffect(() => {
    document.title = titleForPath(pathname);
  }, [pathname]);
}
